import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';

export interface Notification {
  id: string;
  type: string;
  message: string;
  timestamp: string;
  severity?: string;
  details?: Record<string, unknown>;
  source?: string;
  source_id?: number;
  read?: boolean;
}

interface NotificationTypeSettings {
  audit_events?: boolean;
  security_events?: boolean;
  user_actions?: boolean;
  system_events?: boolean;
  bulk_operations?: boolean;
  exports?: boolean;
}

interface QuietHoursSettings {
  enabled?: boolean;
  startTime?: string;
  endTime?: string;
}

interface NotificationFeedResponse {
  items?: Notification[];
  next_cursor?: string | null;
  has_more?: boolean;
}

interface PersistedNotificationState {
  notifications: Notification[];
  cursor: string | null;
}

interface UseNotificationsOptions {
  token: string;
  userId: string;
  panelOpen?: boolean;
  maxNotifications?: number;
  enableBrowserNotifications?: boolean;
  notificationTypes?: NotificationTypeSettings;
  quietHours?: QuietHoursSettings;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  isPaused: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' | 'paused';
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  clearNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  sendMessage: (message: Record<string, unknown>) => void;
  reconnect: () => void;
  refresh: () => void;
}

const STORAGE_KEY_PREFIX = 'notification-center-state-v1';
const DEFAULT_MAX_NOTIFICATIONS = 50;
const toPollInterval = (rawValue: string | undefined, fallback: number): number => {
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
const OPEN_INTERVAL_MS = toPollInterval(import.meta.env.VITE_NOTIFICATION_POLL_OPEN_MS, 5000);
const CLOSED_INTERVAL_MS = toPollInterval(import.meta.env.VITE_NOTIFICATION_POLL_CLOSED_MS, 20000);
const MAX_CATCH_UP_PAGES = 5;

const SETTINGS_TO_EVENT_TYPES: Record<keyof NotificationTypeSettings, string> = {
  audit_events: 'audit_event',
  security_events: 'security_event',
  user_actions: 'user_action',
  system_events: 'system_event',
  bulk_operations: 'bulk_operation_complete',
  exports: 'export_ready',
};

const defaultTypeSettings: NotificationTypeSettings = {
  audit_events: true,
  security_events: true,
  user_actions: true,
  system_events: true,
  bulk_operations: true,
  exports: true,
};

const getStorageKey = (userId: string): string => `${STORAGE_KEY_PREFIX}:${userId || 'anonymous'}`;

const parseHm = (value: string | undefined): number | null => {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) {
    return null;
  }

  const [h, m] = value.split(':').map((part) => Number(part));
  if (Number.isNaN(h) || Number.isNaN(m)) {
    return null;
  }

  return h * 60 + m;
};

const isWithinQuietHours = (quietHours?: QuietHoursSettings): boolean => {
  if (!quietHours?.enabled) {
    return false;
  }

  const start = parseHm(quietHours.startTime);
  const end = parseHm(quietHours.endTime);

  if (start === null || end === null) {
    return false;
  }

  const now = new Date();
  const current = now.getHours() * 60 + now.getMinutes();

  if (start === end) {
    return true;
  }

  if (start < end) {
    return current >= start && current < end;
  }

  // Overnight range, e.g. 22:00 -> 08:00
  return current >= start || current < end;
};

const loadPersistedState = (
  userId: string,
  maxNotifications: number
): PersistedNotificationState => {
  if (!userId || typeof window === 'undefined') {
    return { notifications: [], cursor: null };
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(userId));
    if (!raw) {
      return { notifications: [], cursor: null };
    }

    const parsed = JSON.parse(raw) as PersistedNotificationState;
    const notifications = Array.isArray(parsed.notifications)
      ? parsed.notifications.slice(0, maxNotifications)
      : [];

    return {
      notifications,
      cursor: typeof parsed.cursor === 'string' ? parsed.cursor : null,
    };
  } catch {
    return { notifications: [], cursor: null };
  }
};

const savePersistedState = (
  userId: string,
  notifications: Notification[],
  cursor: string | null
) => {
  if (!userId || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify({ notifications, cursor })
    );
  } catch {
    // Ignore localStorage write failures.
  }
};

const isPageVisibleAndOnline = (): boolean => {
  const visible = typeof document === 'undefined' ? true : !document.hidden;
  const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  return visible && online;
};

export const useNotifications = (options: UseNotificationsOptions): UseNotificationsReturn => {
  const {
    token,
    userId,
    panelOpen = false,
    maxNotifications = DEFAULT_MAX_NOTIFICATIONS,
    enableBrowserNotifications = true,
    notificationTypes = defaultTypeSettings,
    quietHours,
  } = options;

  const normalizedTypeSettings = useMemo(
    () => ({ ...defaultTypeSettings, ...(notificationTypes || {}) }),
    [notificationTypes]
  );

  const enabledFeedTypes = useMemo(() => {
    const enabled: string[] = [];
    (Object.keys(SETTINGS_TO_EVENT_TYPES) as Array<keyof NotificationTypeSettings>).forEach((key) => {
      if (normalizedTypeSettings[key]) {
        enabled.push(SETTINGS_TO_EVENT_TYPES[key]);
      }
    });
    return enabled;
  }, [normalizedTypeSettings]);

  const pollingInterval = panelOpen ? OPEN_INTERVAL_MS : CLOSED_INTERVAL_MS;

  const initialState = useMemo(
    () => loadPersistedState(userId, maxNotifications),
    [userId, maxNotifications]
  );

  const [notifications, setNotifications] = useState<Notification[]>(initialState.notifications);
  const [cursor, setCursor] = useState<string | null>(initialState.cursor);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected' | 'error' | 'paused'
  >('disconnected');
  const [refreshTick, setRefreshTick] = useState(0);

  const cursorRef = useRef<string | null>(initialState.cursor);
  const fetchInFlightRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldPollRef = useRef(true);

  const unreadCount = useMemo(
    () => notifications.reduce((count, notification) => count + (notification.read ? 0 : 1), 0),
    [notifications]
  );

  useEffect(() => {
    const persisted = loadPersistedState(userId, maxNotifications);
    setNotifications(persisted.notifications);
    setCursor(persisted.cursor);
    cursorRef.current = persisted.cursor;
  }, [userId, maxNotifications]);

  useEffect(() => {
    const trimmed = notifications.slice(0, maxNotifications);
    if (trimmed.length !== notifications.length) {
      setNotifications(trimmed);
      return;
    }
    savePersistedState(userId, notifications, cursor);
  }, [notifications, cursor, userId, maxNotifications]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    if (
      enableBrowserNotifications &&
      typeof window !== 'undefined' &&
      'Notification' in window &&
      window.Notification.permission === 'default'
    ) {
      void window.Notification.requestPermission();
    }
  }, [enableBrowserNotifications]);

  const mergeIncomingItems = useCallback(
    (incoming: Notification[]) => {
      if (incoming.length === 0) {
        return;
      }

      setNotifications((prev) => {
        const existing = new Map(prev.map((item) => [item.id, item]));
        const newlyInserted: Notification[] = [];

        incoming.forEach((item) => {
          if (!item?.id) {
            return;
          }

          const prior = existing.get(item.id);
          const merged: Notification = {
            ...item,
            read: prior?.read ?? false,
          };

          if (!prior) {
            newlyInserted.push(merged);
          }

          existing.set(item.id, merged);
        });

        if (
          newlyInserted.length > 0 &&
          enableBrowserNotifications &&
          !isWithinQuietHours(quietHours) &&
          typeof window !== 'undefined' &&
          'Notification' in window &&
          window.Notification.permission === 'granted'
        ) {
          newlyInserted.forEach((notification) => {
            if (notification.read) {
              return;
            }
            try {
              void new window.Notification('Pricing System', {
                body: notification.message,
                icon: '/favicon.ico',
              });
            } catch {
              // Ignore browser notification failures.
            }
          });
        }

        return Array.from(existing.values())
          .sort(
            (a, b) =>
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          )
          .slice(0, maxNotifications);
      });
    },
    [enableBrowserNotifications, maxNotifications, quietHours]
  );

  const fetchFeed = useCallback(async () => {
    if (!token || !userId || enabledFeedTypes.length === 0 || fetchInFlightRef.current) {
      return;
    }

    if (!isPageVisibleAndOnline()) {
      setConnectionStatus('paused');
      setIsConnected(false);
      return;
    }

    fetchInFlightRef.current = true;
    setConnectionStatus('connecting');

    try {
      let nextCursor = cursorRef.current;
      const accumulated: Notification[] = [];

      for (let page = 0; page < MAX_CATCH_UP_PAGES; page += 1) {
        const params = new URLSearchParams();
        params.set('limit', String(Math.min(maxNotifications, 100)));
        params.set('types', enabledFeedTypes.join(','));
        if (nextCursor) {
          params.set('cursor', nextCursor);
        }

        const response = await apiService.get<NotificationFeedResponse>(
          `/api/v1/notifications/feed?${params.toString()}`
        );
        const items = Array.isArray(response.items) ? response.items : [];
        accumulated.push(...items);

        const candidateCursor =
          typeof response.next_cursor === 'string' ? response.next_cursor : nextCursor;
        nextCursor = candidateCursor;

        if (!response.has_more || !response.next_cursor) {
          break;
        }
      }

      if (accumulated.length > 0) {
        mergeIncomingItems(accumulated);
      }

      if (nextCursor !== cursorRef.current) {
        setCursor(nextCursor || null);
      }

      setConnectionStatus('connected');
      setIsConnected(true);
    } catch {
      setConnectionStatus('error');
      setIsConnected(false);
    } finally {
      fetchInFlightRef.current = false;
    }
  }, [enabledFeedTypes, maxNotifications, mergeIncomingItems, token, userId]);

  useEffect(() => {
    shouldPollRef.current = true;

    const schedule = () => {
      if (!shouldPollRef.current) {
        return;
      }

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        void fetchFeed();
        schedule();
      }, pollingInterval);
    };

    const onVisibilityOrOnline = () => {
      if (!shouldPollRef.current) {
        return;
      }
      void fetchFeed();
    };

    const onOffline = () => {
      setConnectionStatus('paused');
      setIsConnected(false);
    };

    void fetchFeed();
    schedule();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityOrOnline);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onVisibilityOrOnline);
      window.addEventListener('offline', onOffline);
    }

    return () => {
      shouldPollRef.current = false;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityOrOnline);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onVisibilityOrOnline);
        window.removeEventListener('offline', onOffline);
      }
    };
  }, [fetchFeed, pollingInterval, refreshTick]);

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((notification) => ({ ...notification, read: true })));
  }, []);

  const clearNotification = useCallback((notificationId: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const refresh = useCallback(() => {
    setRefreshTick((prev) => prev + 1);
  }, []);

  const reconnect = useCallback(() => {
    refresh();
  }, [refresh]);

  const sendMessage = useCallback((_message: Record<string, unknown>) => {
    // No-op in polling mode. Kept for backward compatibility with existing callers.
  }, []);

  return {
    notifications,
    unreadCount,
    isConnected,
    isPaused: connectionStatus === 'paused',
    connectionStatus,
    markAsRead,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
    sendMessage,
    reconnect,
    refresh,
  };
};
